// App-lifecycle seam. iOS suspends the webview in the background — sockets die by design;
// on foreground the app must re-mint its WS ticket, reconnect, rejoin route groups, and refetch. The DOM
// `visibilitychange`/`pageshow` signals fire in the webview, but Capacitor's native `appStateChange` is the
// authoritative foreground signal on device — kept behind this seam so apps/core never imports @capacitor/app
// directly (same posture as token-provider/push: native-only glue, no-op on web).
import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";
import { App } from "@capacitor/app";

/**
 * Invoke `callback` whenever the native app returns to the foreground (`appStateChange` → `isActive: true`).
 * No-op on web (returns a no-op unsubscribe). Returns a SYNCHRONOUS unsubscribe usable directly in a React
 * effect cleanup; it handles the race where the effect is torn down before the async `addListener` resolves.
 */
export function onNativeResume(callback: () => void): () => void {
  if (!Capacitor.isNativePlatform()) return () => {};

  let handle: PluginListenerHandle | null = null;
  let removed = false;

  void App.addListener("appStateChange", (state) => {
    if (state.isActive) callback();
  }).then((h) => {
    if (removed) void h.remove(); // unsubscribed before the listener finished registering
    else handle = h;
  });

  return () => {
    removed = true;
    if (handle) {
      void handle.remove();
      handle = null;
    }
  };
}

/**
 * Invoke `callback` whenever the native app goes to the background (`appStateChange` → `isActive: false`). The
 * mirror of {@link onNativeResume}; no-op on web.
 */
export function onNativeBackground(callback: () => void): () => void {
  if (!Capacitor.isNativePlatform()) return () => {};

  let handle: PluginListenerHandle | null = null;
  let removed = false;

  void App.addListener("appStateChange", (state) => {
    if (!state.isActive) callback();
  }).then((h) => {
    if (removed) void h.remove();
    else handle = h;
  });

  return () => {
    removed = true;
    if (handle) {
      void handle.remove();
      handle = null;
    }
  };
}
